import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;
const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A' };
const DOT = { operational: C.green, degraded: C.amber, down: C.red, standby: C.slate };

export default function StatusPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const load = () => axios.get(`${API}/api/status`).then(r => { setData(r.data); setError(false); }).catch(() => setError(true));
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, []);

  const ok = data?.status === 'operational' && !error;
  const banner = error ? { text: 'Status temporarily unavailable', color: C.amber }
    : ok ? { text: 'All Systems Operational', color: C.green }
    : { text: 'Degraded Performance', color: C.amber };

  return (
    <div className="min-h-screen" style={{ background: '#F5F7FA' }} data-testid="status-page">
      {/* Masthead */}
      <div style={{ background: C.navy }} className="px-6 py-5">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <span className="text-lg font-bold text-white">under<span style={{ color: C.teal }}>AI</span>ter</span>
          <span className="text-[10px] uppercase tracking-[0.2em]" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Georgia, serif' }}>Platform Status</span>
        </div>
      </div>
      <div style={{ height: 3, background: C.teal }} />

      <div className="max-w-3xl mx-auto px-6 py-10 space-y-6">
        {/* Banner */}
        <div className="flex items-center gap-3 p-5 rounded-sm bg-white" style={{ border: `1px solid ${banner.color}40` }} data-testid="status-banner">
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60" style={{ background: banner.color }} />
            <span className="relative inline-flex rounded-full h-3 w-3" style={{ background: banner.color }} />
          </span>
          <h1 className="text-xl font-semibold" style={{ color: C.navy }}>{banner.text}</h1>
          {data && <span className="ml-auto text-xs font-mono" style={{ color: C.slate }}>{data.uptime_90d_pct}% · 90 days</span>}
        </div>

        {/* ARIA today */}
        {data?.aria_today && (
          <div className="bg-white rounded-sm p-5" style={{ border: '1px solid rgba(136,150,168,0.2)' }} data-testid="status-aria-today">
            <p className="text-[10px] uppercase tracking-[0.15em] mb-4" style={{ color: C.slate, fontFamily: 'Georgia, serif' }}>How fast ARIA is deciding today</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                ['Median decision', data.aria_today.median_decision_seconds != null ? `${data.aria_today.median_decision_seconds}s` : '—'],
                ['Decisions today', (data.aria_today.decisions_today ?? 0).toLocaleString()],
                ['Straight-through rate', data.aria_today.stp_rate_today != null ? `${data.aria_today.stp_rate_today}%` : '—'],
                ['Policies bound today', (data.aria_today.policies_bound_today ?? 0).toLocaleString()],
              ].map(([label, value]) => (
                <div key={label}>
                  <p className="text-3xl font-light tracking-tight" style={{ color: C.navy, fontVariantNumeric: 'tabular-nums' }}>{value}</p>
                  <p className="text-[10px] uppercase tracking-[0.12em] mt-1" style={{ color: C.slate, fontFamily: 'Georgia, serif' }}>{label}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Services */}
        {data && (
          <div className="bg-white rounded-sm divide-y divide-[rgba(136,150,168,0.1)]" style={{ border: '1px solid rgba(136,150,168,0.2)' }} data-testid="status-services">
            {data.services.map(s => (
              <div key={s.name} className="flex items-center justify-between px-5 py-3">
                <span className="text-sm font-medium" style={{ color: C.navy }}>{s.name}</span>
                <span className="flex items-center gap-2 text-xs" style={{ color: C.slate }}>
                  {s.detail}
                  <span className="w-2 h-2 rounded-full" style={{ background: DOT[s.status] || C.slate }} />
                  <span className="capitalize font-medium w-20 text-right" style={{ color: DOT[s.status] || C.slate }}>{s.status}</span>
                </span>
              </div>
            ))}
          </div>
        )}

        {/* 90-day uptime bars */}
        {data && (
          <div className="bg-white rounded-sm p-5" style={{ border: '1px solid rgba(136,150,168,0.2)' }} data-testid="status-uptime-bars">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[10px] uppercase tracking-[0.15em]" style={{ color: C.slate, fontFamily: 'Georgia, serif' }}>90-day uptime</p>
              <p className="text-xs font-mono" style={{ color: C.green }}>{data.uptime_90d_pct}%</p>
            </div>
            <div className="flex gap-[2px]">
              {data.uptime_days.map(d => (
                <div key={d.date} title={`${d.date} — ${d.status}`}
                  className="flex-1 h-8 rounded-[1px] transition-colors"
                  style={{ background: d.status === 'operational' ? C.green : C.amber, opacity: d.status === 'operational' ? 0.75 : 1 }} />
              ))}
            </div>
            <div className="flex justify-between mt-1.5 text-[10px]" style={{ color: C.slate }}>
              <span>90 days ago</span><span>Today</span>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between text-[11px]" style={{ color: C.slate }}>
          <span>Auto-refreshes every 30 seconds{data ? ` · checked ${new Date(data.checked_at).toLocaleTimeString('en-AU')}` : ''}</span>
          <Link to="/login" className="hover:underline" style={{ color: C.blue }} data-testid="status-login-link">Sign in →</Link>
        </div>
        <p className="text-[10px] text-center pt-4" style={{ color: '#64748B' }}>Powered by frAIme — AI-Native Underwriting</p>
      </div>
    </div>
  );
}
